import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { IDENTITY_DOCS, IdentityDocumentType } from '~/constants/docs.ts';
import { blockedDomains } from '~/constants/common.ts';
import { ALLOWED_TLDs, UKRAINIAN_OPERATOR_CODES } from '~/constants/common.ts';

export const isAbsentCondition = (schema: Yup.AnySchema, isAbsent: boolean): Yup.AnySchema => {
  if (isAbsent) return schema.notRequired();
  else return schema;
};

export const documentSeriesSchema = (dependentField?: string, isAbsent?: boolean) => {
  return Yup.string()
    .nullable()
    .when(dependentField || [], ([docType], schema) => {
      if (isAbsent) return schema.notRequired();
      if (!docType) return schema.notRequired();

      if (Number(docType?.value) === IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassport]) {
        return schema
          .matches(
            regularExpressions.CYRILLIC_TWO_LETTERS,
            errorMessages.TWO_UPPER_CASE_UKRAINIAN_LETTERS
          )
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (Number(docType?.value) === IDENTITY_DOCS[IdentityDocumentType.ukrainianForeignPassport]) {
        return schema
          .matches(regularExpressions.LATIN_TWO_LETTERS, errorMessages.TWO_LATIN_LETTERS)
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (Number(docType?.value) === IDENTITY_DOCS[IdentityDocumentType.permanentResidencePermit]) {
        return schema.required(errorMessages.FIELD_REQUIRED);
      }
      return schema.notRequired();
    });
};

export const documentNumberSchema = (dependentField?: string, isAbsent?: boolean) => {
  return Yup.string()
    .nullable()
    .when(dependentField || [], ([docType], schema) => {
      if (isAbsent) return schema.notRequired();
      if (!docType) return schema.notRequired();

      if (Number(docType?.value) === IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassport]) {
        return schema
          .matches(regularExpressions.SIX_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(6))
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          })
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (
        Number(docType?.value) ===
        IDENTITY_DOCS[IdentityDocumentType.temporaryResidencePermitPlastic]
      ) {
        return schema
          .matches(regularExpressions.SIX_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(6))
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          })
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (
        Number(docType?.value) ===
        IDENTITY_DOCS[IdentityDocumentType.ukrainianCitizenPassportPlastic]
      ) {
        return schema
          .matches(regularExpressions.NINE_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(9))
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          })
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (Number(docType?.value) === IDENTITY_DOCS[IdentityDocumentType.permanentResidencePermit]) {
        return schema
          .matches(regularExpressions.SIX_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(6))
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          })
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (
        Number(docType?.value) ===
        IDENTITY_DOCS[IdentityDocumentType.permanentResidencePermitPlastic]
      ) {
        return schema
          .matches(regularExpressions.NINE_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(9))
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          })
          .required(errorMessages.FIELD_REQUIRED);
      }
      if (Number(docType?.value) === IDENTITY_DOCS[IdentityDocumentType.ukrainianForeignPassport]) {
        return schema
          .matches(regularExpressions.SIX_DIGITS, errorMessages.MAX_DIGITS_IN_DOC(6))
          .test('not-all-same', errorMessages.INVALID_INPUT_DATA, (value) => {
            if (!value) return true;
            return !regularExpressions.DUPLICATION_NUMBER.test(value);
          })
          .required(errorMessages.FIELD_REQUIRED);
      }
      return schema.notRequired();
    });
};

export const phoneValidation = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .test(
    'phone-validation',
    errorMessages.INVALID_INPUT_DATA,
    function (value: string | undefined): boolean {
      if (!value) return false;
      const cleanPhone = value.replace(/[\s\-()]/g, '');
      if (cleanPhone.startsWith('+380')) {
        if (!/^\+380\d{9}$/.test(cleanPhone)) return false;

        const operatorCode = cleanPhone.substring(3, 6);
        return UKRAINIAN_OPERATOR_CODES.includes(operatorCode);
      } else {
        return /^\+\d{1,3}\d{6,}$/.test(cleanPhone);
      }
    }
  );

export const emailValidation = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .email(errorMessages.INVALID_EMAIL_FORMAT)
  .test(
    'blocked-domain',
    errorMessages.BLOCKED_EMAIL_DOMAIN,
    function (value: string | undefined): boolean {
      if (!value || !value.includes('@')) return true;
      const domain = value.split('@')[1];
      return !blockedDomains.some(
        (blocked: string) => domain === blocked || domain?.endsWith(`.${blocked}`)
      );
    }
  )
  .test(
    'allowed-tld',
    errorMessages.INVALID_EMAIL_FORMAT,
    function (value: string | undefined): boolean {
      if (!value) return true;
      const email = value.toLowerCase();
      return ALLOWED_TLDs.some((tld: string) => email.endsWith(tld));
    }
  );

const detectLanguage = (value: string) => {
  if (!value) return;
  const isUkr = regularExpressions.UKR_CHARACTERS_ONLY.test(value);
  const isLatin = regularExpressions.LATIN_CHARACTERS_ONLY.test(value);

  if (isUkr) return 'ukr';
  if (isLatin) return 'latin';

  return 'mixed';
};

const checkLanguageConsistency = function (
  this: Yup.TestContext,
  value: string | null | undefined
) {
  if (!value) return true;

  const currentLang = detectLanguage(value);
  if (currentLang === 'mixed') {
    return this.createError({
      message: errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
    });
  }

  const { lastName, firstName, middleName } = this.parent as {
    lastName?: string | null;
    firstName?: string | null;
    middleName?: string | null;
  };

  const orderedFields = [lastName, firstName, middleName].filter(
    (f) => f && f.trim() !== ''
  ) as string[];

  if (orderedFields.length === 1) return true;

  const firstFieldLang = detectLanguage(orderedFields[0] as string);

  if (firstFieldLang === 'mixed') {
    return this.createError({
      message: errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
    });
  }

  if (!firstFieldLang) return true;

  if (currentLang !== firstFieldLang) {
    return this.createError({
      message:
        firstFieldLang === 'latin'
          ? errorMessages.LATIN_CHARACTERS_ONLY
          : errorMessages.UKR_CHARACTERS_ONLY,
    });
  }

  return true;
};

export const latinAndUkrCharactersOnly = Yup.string()
  .nullable()
  .test(
    'language-consistency',
    errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY,
    checkLanguageConsistency
  );
